import axios from "../api";
import {
  AddAttachmentsRequest,
  AddAttachmentsResponse,
  AddCommentRequest,
  AddCommentResponse,
  AllCoreReturnsRequest,
  AllCoreReturnsResponse,
  CancelCoreReturnRequest,
  CancelReturnRequest,
  CancelReturnResponse,
  ConfirmShipmentRequest,
  CoreReturnDetails,
  CreateCoreReturnRequest,
  CreateReturnRequest,
  CreateReturnResponse,
  CreateSurplusReturnRequest,
  DailyReturnsReviewRequest,
  DownloadAttachmentRequest,
  DownloadAttachmentResponse,
  DownloadCoreAttachmentResponse,
  DownloadCoreAuthFormResponse,
  DownloadCoreAuthRequest,
  EligibilitiesRequest,
  EligibilitiesResponse,
  FetchReturnReasonRequest,
  GetSurplusReturnAddressesRequest,
  InspectionResultsRequest,
  InspectionResultsResponse,
  ReturnAddresses,
  ReturnDetailsRequest,
  ReturnReasonResponse,
  ReturnsApi,
  ReturnsRequest,
  ReturnsRequestV2,
  ReturnsResponse,
  SaveDealerPreparationRequest,
  SurplusCsooOptions,
  SurplusReturnDetailsRequest,
  SurplusReturnDetailsResponse,
  UpdateSurplusReturnCsooRequest,
  UpdateSurplusReturnRequest,
  ValidateEligibilitiesRequest,
  ValidateNfeRequest,
  ValidateNfeResponse,
} from "./returnsInterfaces";

const returnsApi: ReturnsApi = {
  validateNfe: async ({
    returnServiceUrl,
    returnId,
    accountId,
    accessToken,
    file,
  }: ValidateNfeRequest): Promise<ValidateNfeResponse> => {
    const data = new FormData();
    data.append("nfeFile", file);

    return axios
      .postWithRetries(
        `${returnServiceUrl}/api/v1/returns/validateNfe/${returnId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
            "x-account-id": accountId,
          },
        }
      )
      .then((response: any) => response.data)
      .catch((errorResponse: any) => {
        if (
          !errorResponse.response.data.errors &&
          errorResponse.response.status > 299
        ) {
          throw errorResponse;
        }
        return errorResponse.response.data;
      });
  },
  getReturnReasonDetails: ({
    returnsServiceUrl,
    accountId,
    accessToken,
    lang,
  }: FetchReturnReasonRequest): Promise<ReturnReasonResponse> =>
    axios
      .getWithRetries(`${returnsServiceUrl}/api/v1/returns/reasons`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "account-id": accountId,
        },
        params: lang ? { lang } : undefined,
      })
      .then((response: any) => response.data),

  returnAddresses: async (
    baseUrl: string,
    invoiceNumber: string,
    token: string
  ): Promise<ReturnAddresses> => {
    const response = await axios.getWithRetries(
      `${baseUrl}/api/v1/returns/addresses`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          invoiceNumber,
        },
      }
    );
    return response.data;
  },

  createReturn: async ({
    returnServiceUrl,
    createReturn,
    accessToken,
    lang,
    attachments,
  }: CreateReturnRequest): Promise<CreateReturnResponse> => {
    const jsonBlob = new Blob([JSON.stringify(createReturn)], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);
    if (attachments) {
      attachments.forEach((file) => {
        data.append("files", file);
      });
    }

    const response = await axios.postWithRetries(
      `${returnServiceUrl}/api/v1/returns`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        params: lang ? { lang } : undefined,
      }
    );

    return response.data;
  },

  getAllReturns: async ({
    returnsServiceUrl,
    startDate,
    endDate,
    returnType,
    accountId,
    accessToken,
    includeStatusCount,
    status,
    returnId,
    invoiceNumber,
    nfeNumber,
    dealerReference,
    partNumber,
    pageNumber,
    pageSize,
    sortField,
    sortDirection,
    withLeadingZeros,
  }: ReturnsRequest): Promise<ReturnsResponse> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-account-id": accountId,
        },
        params: {
          startDate,
          endDate,
          returnType,
          includeStatusCount,
          status,
          returnId,
          invoiceNumber,
          nfeNumber,
          dealerReference,
          partNumber,
          pageNumber,
          pageSize,
          sortField,
          sortDirection,
          withLeadingZeros,
        },
      }
    );
    return response.data;
  },

  getAllReturnsV2: async ({
    returnsServiceUrl,
    accessToken,
    ...payload
  }: ReturnsRequestV2): Promise<ReturnsResponse> => {
    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v2/returns/search`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },

  getAllCoreReturns: async (
    allCoreReturnsRequest: AllCoreReturnsRequest
  ): Promise<AllCoreReturnsResponse> => {
    const { returnsServiceUrl, accessToken, accountId, ...params } =
      allCoreReturnsRequest;

    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/allCoreReturns`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "account-id": accountId,
        },
        params,
      }
    );

    return response.data;
  },

  getEligibilities: async ({
    returnsServiceUrl,
    accountId,
    accessToken,
    ...params
  }: EligibilitiesRequest): Promise<EligibilitiesResponse> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/eligibilities`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "account-id": accountId,
        },
        params,
      }
    );
    return response.data;
  },

  getEligibilitiesAsExcel: async ({
    returnsServiceUrl,
    accountId,
    accessToken,
    ...params
  }: EligibilitiesRequest): Promise<any> =>
    axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/eligibilities`,
      {
        responseType: "blob",
        headers: {
          "account-id": accountId,
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/vnd.ms-excel",
        },
        params,
      }
    ),

  validateCorePartsEligibility: async ({
    returnsServiceUrl,
    accountId,
    accessToken,
    coreParts,
  }: ValidateEligibilitiesRequest): Promise<EligibilitiesResponse> => {
    const jsonBlob = new Blob([JSON.stringify({ accountId, coreParts })], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("validateCorePartsRequest", jsonBlob);
    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/eligibilities/validate`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  cancelReturn: async ({
    returnId,
    returnsServiceUrl,
    accessToken,
  }: CancelReturnRequest): Promise<CancelReturnResponse> => {
    const response = await axios.putWithRetries(
      `${returnsServiceUrl}/api/v1/returns`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          returnId,
        },
      }
    );
    return response.data;
  },

  cancelSurplusReturn: async ({
    returnId,
    returnsServiceUrl,
    accessToken,
    accountId,
  }: CancelReturnRequest): Promise<CancelReturnResponse> => {
    const response = await axios.putWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(accountId ? { "x-account-id": accountId } : {}),
        },
        params: {
          returnId,
        },
      }
    );
    return response.data;
  },

  cancelCoreReturn: async ({
    coreReturnId,
    dealerId,
    returnsServiceUrl,
    accessToken,
  }: CancelCoreReturnRequest): Promise<CancelReturnResponse> => {
    const response = await axios.putWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          coreReturnId,
          dealerId,
        },
      }
    );
    return response.data;
  },

  getReturnDetails: async ({
    returnId,
    returnsServiceUrl,
    accessToken,
    accountId,
    lang,
    withLeadingZeros,
  }: ReturnDetailsRequest) => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/${returnId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-account-id": accountId,
        },
        params: {
          lang,
          withLeadingZeros,
        },
      }
    );
    return response.data;
  },

  getSurplusReturnDetails: async ({
    returnId,
    returnsServiceUrl,
    accessToken,
    accountId,
    lang,
  }: SurplusReturnDetailsRequest): Promise<SurplusReturnDetailsResponse> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus/${returnId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-account-id": accountId,
        },
        params: {
          lang,
        },
      }
    );
    return response.data;
  },

  getSurplusReturnCsooOptions: async ({
    returnId,
    returnsServiceUrl,
    accessToken,
    accountId,
    lang,
  }: SurplusReturnDetailsRequest): Promise<SurplusCsooOptions[]> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus/${returnId}/csoo-options`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-account-id": accountId,
        },
        params: {
          lang,
        },
      }
    );
    return response.data;
  },

  getCoreReturnDetails: async ({
    returnId,
    returnsServiceUrl,
    accessToken,
    accountId,
    lang,
  }: ReturnDetailsRequest): Promise<CoreReturnDetails> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/${returnId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          lang,
          dealerId: accountId,
        },
      }
    );
    return mapToCoreReturnDetails(response.data);
  },

  addAttachments: async ({
    returnsServiceUrl,
    returnId,
    accessToken,
    attachments,
    nfeReferenceDate,
    nfeReferenceNumber,
    passAccountIdInHeaders,
    accountId,
  }: AddAttachmentsRequest): Promise<AddAttachmentsResponse> => {
    const jsonBlob = new Blob(
      [
        JSON.stringify({
          returnId,
          nfeReferenceDate,
          nfeReferenceNumber,
        }),
      ],
      {
        type: "application/json",
      }
    );
    const data = new FormData();
    data.append("body", jsonBlob);
    if (attachments) {
      attachments.forEach((file) => {
        data.append("files", file);
      });
    }

    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/addAttachments`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
          ...(passAccountIdInHeaders ? { "x-account-id": accountId } : {}),
        },
      }
    );

    return response.data;
  },

  addComment: async ({
    returnsServiceUrl,
    returnId,
    accessToken,
    comment,
    passAccountIdInHeaders,
    accountId,
  }: AddCommentRequest): Promise<AddCommentResponse> => {
    const jsonBlob = new Blob([JSON.stringify({ returnId, comment })], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);

    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/addComment`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "string",
          ...(passAccountIdInHeaders ? { "x-account-id": accountId } : {}),
        },
      }
    );

    return { ...response.data, status: response.status };
  },

  downloadAttachment: async ({
    returnsServiceUrl,
    returnId,
    phioObjid,
    accessToken,
  }: DownloadAttachmentRequest): Promise<DownloadAttachmentResponse> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/downloadAttachment`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          returnId,
          phioObjid,
        },
      }
    );

    return { ...response.data, status: response.status };
  },

  reviewSurplusLineItems: async ({
    returnServiceUrl,
    accountId,
    accessToken = "",
    language: lang = "en_US",
    lineItems,
  }) => {
    const response = await axios.postWithRetries(
      `${returnServiceUrl}/api/v1/returns/surplus/review`,
      lineItems,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "x-account-id": accountId,
        },
        params: lang ? { lang } : undefined,
      }
    );
    return response.data;
  },

  reviewDailyReturnsLineItems: async ({
    returnServiceUrl,
    accountId,
    accessToken = "",
    language: lang = "en_US",
    invoiceNumber,
    lineItems,
  }: DailyReturnsReviewRequest) => {
    const response = await axios.postWithRetries(
      `${returnServiceUrl}/api/v1/returns/review`,
      {
        dealerId: accountId,
        invoiceNumber,
        lineItems,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "x-account-id": accountId,
        },
        params: lang ? { lang } : undefined,
      }
    );
    return response.data.lineItems;
  },

  createSurplusReturn: async ({
    returnServiceUrl,
    accessToken,
    submitRequest,
    attachments,
  }: CreateSurplusReturnRequest) => {
    const jsonBlob = new Blob([JSON.stringify(submitRequest)], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);
    if (attachments) {
      attachments.forEach((file) => {
        data.append("files", file);
      });
    }

    const response = await axios.postWithRetries(
      `${returnServiceUrl}/api/v1/returns/surplus`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  updateSurplusReturn: async ({
    returnServiceUrl,
    accessToken,
    updateRequest,
  }: UpdateSurplusReturnRequest) => {
    const jsonBlob = new Blob([JSON.stringify(updateRequest)], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);

    const response = await axios.patchWithRetries(
      `${returnServiceUrl}/api/v1/returns/surplus`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
          ...(updateRequest.accountId && {
            "x-account-id": updateRequest.accountId,
          }),
        },
      }
    );
    return response.data;
  },

  updateSurplusReturnCsoo: async ({
    returnsServiceUrl,
    accessToken,
    accountId,
    updateRequest,
  }: UpdateSurplusReturnCsooRequest): Promise<CreateReturnResponse> => {
    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus/save-csoo`,
      updateRequest,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "x-account-id": accountId,
        },
      }
    );
    return response.data;
  },

  createCoreReturn: async ({
    returnServiceUrl,
    accessToken,
    coreReturnsPayload,
    attachments,
  }: CreateCoreReturnRequest): Promise<CreateReturnResponse> => {
    const jsonBlob = new Blob([JSON.stringify(coreReturnsPayload)], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);
    if (attachments) {
      attachments.forEach((file) => {
        data.append("files", file);
      });
    }

    const response = await axios.postWithRetries(
      `${returnServiceUrl}/api/v1/returns/core`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  getSurplusReturnAddresses: async ({
    returnServiceUrl,
    accessToken,
    dealerId,
    returnId,
  }: GetSurplusReturnAddressesRequest): Promise<ReturnAddresses> => {
    const response = await axios.getWithRetries(
      `${returnServiceUrl}/api/v1/returns/surplus/addresses`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          dealerId,
          returnId,
        },
      }
    );
    return response.data;
  },

  addCommentForSurplusReturn: async ({
    returnsServiceUrl,
    returnId,
    accessToken,
    comment,
    accountId,
  }: AddCommentRequest): Promise<AddCommentResponse> => {
    const data = { returnId, comment };
    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus/comments`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          ...(accountId && { "x-account-id": accountId }),
        },
      }
    );

    return { ...response.data, status: response.status };
  },

  addCommentForCoreReturn: async ({
    returnsServiceUrl,
    returnId,
    accessToken,
    comment,
  }: AddCommentRequest): Promise<AddCommentResponse> => {
    const data = { returnId, comment };
    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/comments`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return { ...response.data, status: response.status };
  },

  addAttachmentsForSurplusReturn: async ({
    returnsServiceUrl,
    returnId,
    accessToken,
    attachments,
    accountId,
  }: AddAttachmentsRequest): Promise<AddAttachmentsResponse> => {
    const jsonBlob = new Blob([JSON.stringify({ returnId })], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);
    if (attachments) {
      attachments.forEach((file) => {
        data.append("files", file);
      });
    }

    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus/attachments`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
          ...(accountId && { "x-account-id": accountId }),
        },
      }
    );

    return response.data;
  },

  addAttachmentsForCoreReturn: async ({
    returnsServiceUrl,
    returnId,
    accessToken,
    attachments,
  }: AddAttachmentsRequest): Promise<AddAttachmentsResponse> => {
    const jsonBlob = new Blob([JSON.stringify({ returnId })], {
      type: "application/json",
    });
    const data = new FormData();
    data.append("body", jsonBlob);
    if (attachments) {
      attachments.forEach((file) => {
        data.append("files", file);
      });
    }

    const response = await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/attachments`,
      data,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        params: {
          returnId,
        },
      }
    );

    return response.data;
  },

  downloadAttachmentForSurplusReturn: async ({
    returnsServiceUrl,
    returnId,
    phioObjid,
    accessToken,
  }: DownloadAttachmentRequest): Promise<DownloadAttachmentResponse> => {
    const response = await axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/surplus/attachment`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          returnId,
          phioObjid,
        },
      }
    );

    return { ...response.data, status: response.status };
  },

  downloadAttachmentForCoreReturn: async ({
    returnsServiceUrl,
    returnId,
    phioObjid,
    accessToken,
  }: DownloadAttachmentRequest): Promise<DownloadCoreAttachmentResponse> =>
    axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/downloadAttachment`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          returnId,
          phioObjid,
        },
        responseType: "blob",
      }
    ),

  downloadCoreAuthFormAttachment: async ({
    returnsServiceUrl,
    returnId,
    shipToId,
    accessToken,
    lang,
  }: DownloadCoreAuthRequest): Promise<DownloadCoreAuthFormResponse> =>
    axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/core/coreAuthForm`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          returnId,
          shipToId,
          lang,
        },
        responseType: "blob",
      }
    ),

  getDailyReturnInspectionResults: async ({
    returnId,
    accountId,
    returnsServiceUrl,
    accessToken,
    lang,
    withLeadingZeros,
  }: InspectionResultsRequest): Promise<InspectionResultsResponse> =>
    axios.getWithRetries(
      `${returnsServiceUrl}/api/v1/returns/${returnId}/inspection-results`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-account-id": accountId,
        },
        params: {
          lang,
          withLeadingZeros,
        },
      }
    ),

  saveDealerPreparation: async ({
    returnsServiceUrl,
    returnId,
    accountId,
    accessToken,
    request,
  }: SaveDealerPreparationRequest): Promise<void> => {
    await axios.patchWithRetries(
      `${returnsServiceUrl}/api/v1/returns/${returnId}/save-dealer-preparation`,
      request,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "x-account-id": accountId,
        },
      }
    );
  },

  confirmShipment: async ({
    returnsServiceUrl,
    accountId,
    accessToken,
    request,
  }: ConfirmShipmentRequest): Promise<void> => {
    await axios.postWithRetries(
      `${returnsServiceUrl}/api/v1/returns/confirmShipment`,
      request,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "x-account-id": accountId,
        },
      }
    );
  },
};

export default returnsApi;

const mapToCoreReturnDetails = (response: any): CoreReturnDetails => ({
  coreReturnId: response.coreReturnId,
  returnType: response.returnType,
  dealerReference: response.dealerReference,
  currency: response.currency,
  returnDate: response.returnDate,
  headerStatus: response.headerStatus,
  headerStatusDescription: response.headerStatusDescription,
  shipFromAddress: {
    name: response.shipFromName,
    street: response.shipFromStreet,
    city: response.shipFromCity,
    postCode: response.shipFromPostCode,
    region: response.shipFromRegion,
    country: response.shipFromCountry,
  },
  coreReturnItems: response.coreReturnItems.map((item: any) => ({
    partNumber: item.partNumber,
    partDescription: item.partDescription,
    purchasedSerialNumber: item.purchasedSerialNumber,
    returnedSerialNumber: item.returnedSerialNumber,
    warrantyClaimNumber: item.warrantyClaimNumber,
    billingNumber: item.billingNumber,
    billingDate: item.billingDate,
    corePrice: item.corePrice,
    currency: item.currency,
    itemStatus: item.itemStatus,
    itemStatusDescription: item.itemStatusDescription,
    shipToAddress: {
      name: item.shipToName,
      street: item.shipToStreet,
      city: item.shipToCity,
      postCode: item.shipToPostCode,
      region: item.shipToRegion,
      country: item.shipToCountry,
    },
  })),
  coreReturnComments: response.coreReturnComments,
  coreReturnAttachments: response.coreReturnAttachments,
});
